import type { ElementType } from 'react';
import {
  MdDashboard, MdDescription, MdCategory, MdPeople, MdSettings, MdAssessment,
  MdGroup, MdPayments, MdShoppingBag, MdLocalShipping, MdAccountBalance,
  MdAssignmentReturn, MdPointOfSale, MdBusiness, MdWarehouse, MdShoppingBasket,
  MdSwapHoriz, MdInventory, MdReceiptLong, MdPerson, MdList,
} from 'react-icons/md';

// Visibility flags keep their existing meaning; the Guards in App.tsx stay the authority.
export interface NavFlags {
  adminOnly?: boolean;
  hideForDEO?: boolean;
  manageOnly?: boolean;            // admin or supervisor
  requiresReportAccess?: boolean;  // admin, or any report permission
  reportKey?: string;              // admin, or this key in user.reportPermissions (REPORT_PERMISSIONS)
}
export interface NavLeaf extends NavFlags { label: string; to: string; icon: ElementType; end?: boolean }
export interface NavGroup extends NavFlags { label: string; icon: ElementType; to?: string; children?: NavLeaf[] }

// Single source of truth for the sidebar: a group with `to` is a plain link, otherwise it collapses.
export const NAV: NavGroup[] = [
  { label: 'Dashboard', icon: MdDashboard, to: '/dashboard' },
  {
    label: 'Sales', icon: MdReceiptLong,
    children: [
      { label: 'Quotations',  to: '/quotations',   icon: MdDescription },
      { label: 'Orders',      to: '/orders',       icon: MdShoppingBag,      hideForDEO: true },
      { label: 'Dispatch',    to: '/dispatch',     icon: MdLocalShipping,    hideForDEO: true },
      { label: 'Payments',    to: '/payments',     icon: MdPayments,         hideForDEO: true },
      { label: 'Returns',     to: '/returns',      icon: MdAssignmentReturn, hideForDEO: true },
      { label: 'Direct Sale', to: '/direct-sales', icon: MdPointOfSale },
    ],
  },
  {
    label: 'Purchases', icon: MdShoppingBasket, hideForDEO: true,
    children: [
      { label: 'Purchase Orders', to: '/purchase-orders', icon: MdShoppingBasket, hideForDEO: true },
      { label: 'Vendors',         to: '/vendors',         icon: MdBusiness,       hideForDEO: true },
    ],
  },
  {
    label: 'Inventory', icon: MdInventory,
    children: [
      { label: 'Categories', to: '/categories', icon: MdCategory, manageOnly: true },
      { label: 'Products',       to: '/products',        icon: MdCategory },
      { label: 'Warehouses',     to: '/warehouses',      icon: MdWarehouse, hideForDEO: true },
      { label: 'Stock Transfer', to: '/stock-transfers', icon: MdSwapHoriz, manageOnly: true },
    ],
  },
  {
    label: 'Clients', icon: MdPeople,
    children: [
      { label: 'Client List', to: '/clients',   icon: MdPerson },
      { label: 'Statements',  to: '/statement', icon: MdAccountBalance, hideForDEO: true },
    ],
  },
  {
    label: 'Reports', icon: MdAssessment, requiresReportAccess: true,
    children: [
      { label: 'All Reports',         to: '/reports',             icon: MdList, end: true },
      { label: 'Outstanding / Aging', to: '/reports/outstanding',   icon: MdAssessment, reportKey: 'outstanding' },
      { label: 'Product Sales',       to: '/reports/product-sales', icon: MdAssessment, reportKey: 'product-sales' },
      { label: 'Current Stock',       to: '/reports/current-stock', icon: MdAssessment, reportKey: 'current-stock' },
      { label: 'Low Stock',           to: '/reports/low-stock',     icon: MdAssessment, reportKey: 'low-stock' },
      { label: 'Traceability',        to: '/reports/traceability',  icon: MdAssessment, reportKey: 'traceability' },
      { label: 'Purchase Orders',     to: '/reports/po',            icon: MdAssessment, reportKey: 'po' },
    ],
  },
  {
    label: 'Settings', icon: MdSettings,
    children: [
      { label: 'Settings', to: '/admin/settings', icon: MdSettings, adminOnly: true },
      { label: 'Users',    to: '/admin/users',    icon: MdGroup,    adminOnly: true },
    ],
  },
];

export interface NavViewer { isAdmin: boolean; isDEO: boolean; canManageAll: boolean; reportPermissions?: string[] }

export function navVisible(item: NavFlags, viewer: NavViewer): boolean {
  if (item.adminOnly && !viewer.isAdmin) return false;
  if (item.hideForDEO && viewer.isDEO) return false;
  if (item.manageOnly && !viewer.canManageAll) return false;
  if (item.requiresReportAccess && !viewer.isAdmin && !viewer.reportPermissions?.length) return false;
  if (item.reportKey && !viewer.isAdmin && !viewer.reportPermissions?.includes(item.reportKey)) return false;
  return true;
}

// The visible menu for one viewer; a group whose children are all hidden disappears.
export function visibleNav(viewer: NavViewer): NavGroup[] {
  return NAV.filter(group => navVisible(group, viewer)).flatMap(group => {
    if (!group.children) return [group];
    const children = group.children.filter(child => navVisible(child, viewer));
    return children.length ? [{ ...group, children }] : [];
  });
}

const leafMatches = (leaf: NavLeaf, pathname: string) =>
  pathname === leaf.to || (!leaf.end && pathname.startsWith(`${leaf.to}/`));

export const activeGroup = (groups: NavGroup[], pathname: string) =>
  groups.find(group => group.children?.some(child => leafMatches(child, pathname)))?.label;

// Remembered open groups; storage can be unavailable (private mode, blocked site data).
export const NAV_OPEN_KEY = 'watson:nav-open';
export function readOpenGroups(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(NAV_OPEN_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((label): label is string => typeof label === 'string') : [];
  } catch { return []; }
}
export function writeOpenGroups(labels: string[]) {
  try { localStorage.setItem(NAV_OPEN_KEY, JSON.stringify(labels)); } catch { /* not persisted */ }
}

// Whether a viewer may open a page, by the menu entry that owns it (the same flags as the route Guards).
export function canOpen(path: string, viewer: NavViewer): boolean {
  const owners = NAV.flatMap(group => (group.children ?? (group.to ? [{ ...group, to: group.to }] : []))
    .filter(leaf => path === leaf.to || path.startsWith(`${leaf.to}/`)).map(leaf => ({ group, leaf: leaf as NavLeaf })));
  const owner = owners.sort((a, b) => b.leaf.to.length - a.leaf.to.length)[0];
  return !!owner && navVisible(owner.group, viewer) && navVisible(owner.leaf, viewer);
}
